import { FC, useState } from 'react';
import { useFabricEditor } from '../../lib/FabricJs';
import {
  Button as DialogButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { AssetFileType, AssetsResponse } from '../../../../types/assets.types';
import { getStaticFileUrl } from '../../../../util/getStaticFileUrl';
import { useHandleHistory } from '../../lib/FabricJs/hooks/useHandleHistory';
import {
  getSlideVideos,
  MAX_VIDEOS_PER_SLIDE,
  replaceVideo,
} from '../../lib/FabricJs/utils/handleVideo';
import MediaGrid from './media/MediaGrid';
import { useMediaLibrary } from './media/useMediaLibrary';

// What the editor needs to add a video.
interface VideoAsset {
  url: string;
  fileType: string;
  assetId: string;
  thumbnailUrl?: string;
}

const VideoWidget: FC = () => {
  const { editor, canvas } = useFabricEditor();
  const { handleHistory } = useHandleHistory();
  const library = useMediaLibrary(AssetFileType.Video);
  const [pending, setPending] = useState<VideoAsset | null>(null);

  const onPick = (asset: AssetsResponse) => {
    const video = {
      url: getStaticFileUrl(asset.path),
      fileType: asset.fileType,
      assetId: asset._id,
      thumbnailUrl: asset.thumbnailUrl,
    };
    if (canvas && getSlideVideos(canvas).length >= MAX_VIDEOS_PER_SLIDE) {
      setPending(video);
      return;
    }
    editor?.addVideo(video);
  };

  const replace = async () => {
    const video = pending;
    setPending(null);
    if (!canvas || !video) return;
    // Slides from before the limit can have several videos.
    const active = canvas.getActiveObject();
    const current =
      active?.data?.type === 'video' ? active : getSlideVideos(canvas)[0];
    if (!current) return;
    await replaceVideo(canvas, current, video);
    handleHistory(canvas);
  };

  return (
    <>
      <MediaGrid kind="video" library={library} onPick={onPick} />
      <Dialog
        open={!!pending}
        onClose={() => setPending(null)}
        aria-labelledby="replace-video-title"
      >
        <DialogTitle id="replace-video-title">
          Replace the video on this slide?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            A slide can have one video. The new video takes the place and size
            of the current one.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <DialogButton onClick={() => setPending(null)}>Cancel</DialogButton>
          <DialogButton variant="contained" onClick={replace}>
            Replace video
          </DialogButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default VideoWidget;
